/**
 * The page tree (`060-notebook-tree.md`).
 *
 * Owns: optimistic creation, inline rename, drag-to-move/reorder with a drop indicator,
 * archive, expand/collapse, and the full WAI-ARIA `tree` keyboard map (§6).
 *
 * Drag-and-drop is the HTML5 events directly. `020-tech-stack.md` lists no DnD dependency
 * and none is to be added: a tree with a drop indicator is a few dozen lines of pointer
 * maths, and a library here buys ceremony, not correctness (§5).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ChevronRight, Plus } from 'lucide-react';
import type { TreePage } from '@/api/endpoints/pages';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/sonner';
import { useHotkeys, type Hotkey } from '@/hooks/useHotkeys';
import { cn } from '@/lib/utils';
import { ulid } from '@/lib/ulid';
import { useTranslation } from '@/lib/i18n';
import { usePendingEntities } from '@/offline/usePendingEntities';
import { usePendingPages } from '@/offline/usePendingPages';
import {
  expandAll,
  pruneExpanded,
  setExpanded,
  toggleExpanded,
  useExpanded,
} from '@/stores/treeExpanded';
import { TreeRow } from '@/features/notebook/TreeRow';
import {
  ancestorIds,
  appendPosition,
  dropZoneFor,
  placementFor,
  positionAfter,
  subtreeIds,
  visibleRows,
  type DropZone,
} from '@/features/notebook/treeModel';
import {
  useArchivePage,
  useCreatePage,
  useMovePage,
  useTree,
  useUpdatePage,
} from '@/features/notebook/usePageMutations';

interface DragState {
  id: string;
  /** The dragged page's own subtree — every id in it is an invalid drop target (§2). */
  forbidden: ReadonlySet<string>;
  target: { id: string; zone: DropZone } | null;
}

const AUTO_EXPAND_MS = 600;
const AUTO_SCROLL_PX = 40;

export function Tree() {
  const navigate = useNavigate();
  const { pageId: activeId } = useParams<{ pageId: string }>();
  const { t, plural } = useTranslation();

  const tree = useTree();
  const expanded = useExpanded();
  const pending = usePendingEntities();

  // Destructured to `mutate`, deliberately. `useMutation` returns a NEW result object on
  // every render, so a `useCallback` that depends on the mutation OBJECT is re-created every
  // render — which silently un-memoizes every row that receives it, and puts us right back
  // to re-rendering all 500 rows on every keystroke. `mutate` itself is referentially stable
  // (TanStack v5 binds it), so depending on that, and only that, is what keeps the row
  // handlers stable.
  const { mutate: createPageMutate } = useCreatePage();
  const { mutate: updatePage } = useUpdatePage();
  const { mutate: movePage } = useMovePage();
  const { mutate: archivePage } = useArchivePage();

  const [open, setOpen] = useState(true);
  const [focused, setFocused] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; isNew: boolean; row?: TreePage } | null>(
    null,
  );
  const [drag, setDrag] = useState<DragState | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<{ page: TreePage; count: number } | null>(
    null,
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typeAhead = useRef<{ buffer: string; at: number }>({ buffer: '', at: 0 });

  /**
   * The server's tree, plus any page whose `page.create` is still sitting in the outbox.
   *
   * The optimistic write in `useCreatePage` is not enough on its own: it lives in the query
   * cache, and the next refetch of the tree overwrites the cache with a server answer that —
   * if the `POST` has not drained yet — does not contain the new page. The row disappears,
   * taking the inline title the user is in the middle of typing with it (`usePendingPages.ts`
   * has the full story). `060` §6 says the row **stays**; this is what makes it stay.
   */
  const pendingCreates = usePendingPages();

  const pages: readonly TreePage[] = useMemo(() => {
    const server = tree.data?.pages ?? [];
    const known = new Set(server.map((page) => page.id));

    // Everything the outbox is still holding — the durable case: created while offline, or
    // created a moment ago and not drained yet. Survives a reload, because the outbox does.
    const extra = pendingCreates.filter((page) => !known.has(page.id));

    // …and the row currently being renamed, which must be on screen for as long as the user is
    // typing into it, whatever the server or the outbox believe. This closes the last gap: the
    // create has drained (so the outbox no longer holds it) but the tree refetch it triggered
    // has not come back yet (so the server list does not have it either).
    const edited = editing?.row;
    if (edited !== undefined && !known.has(edited.id) && !extra.some((p) => p.id === edited.id)) {
      extra.push(edited);
    }

    return extra.length === 0 ? server : [...server, ...extra];
  }, [tree.data, pendingCreates, editing]);
  const rows = useMemo(() => visibleRows(pages, expanded), [pages, expanded]);

  // Mirrors of the state the drag handlers read. A `dragover` fires many times a second; if
  // its handler were re-created whenever `drag` or `expanded` changed, every row's props
  // would change with it and all 500 would re-render on every pointer move — the exact
  // stutter this component is built to avoid.
  const pagesRef = useRef(pages);
  const expandedRef = useRef(expanded);
  const dragRef = useRef(drag);
  const activeIdRef = useRef(activeId);
  const focusedRef = useRef(focused);

  useEffect(() => {
    pagesRef.current = pages;
  }, [pages]);
  useEffect(() => {
    expandedRef.current = expanded;
  }, [expanded]);
  useEffect(() => {
    dragRef.current = drag;
  }, [drag]);
  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);
  useEffect(() => {
    focusedRef.current = focused;
  }, [focused]);

  // §8: we do not virtualize in v1, and we do not build an abstraction "so we can add it
  // later". We DO ship the instrumentation that tells us the day has come.
  useEffect(() => {
    if (import.meta.env.DEV && rows.length > 500) {
      console.warn(`[tree] ${String(rows.length)} visible rows — time to virtualize (060 §8).`);
    }
  }, [rows.length]);

  // The expanded set is pruned against the fetched tree on every load, so the key does not
  // grow forever; and the ancestors of the open page are auto-expanded, so opening a deep
  // page from a URL or the palette REVEALS it (§5).
  useEffect(() => {
    if (pages.length === 0) return;
    pruneExpanded(new Set(pages.map((page) => page.id)));
    if (activeId !== undefined) expandAll(ancestorIds(pages, activeId));
  }, [pages, activeId]);

  /* ── Creation (§6) ───────────────────────────────────────────────────────── */

  /**
   * DO NOT PLACE A PAGE IN A TREE YOU HAVE NOT SEEN.
   *
   * `Mod+N` is registered by this component, but the hotkeys are live BEFORE the tree query
   * resolves — the sidebar renders "Loading pages…" and the keyboard still works. Pressed in
   * that window, `appendPosition(pages, null)` reads an EMPTY array, concludes there are no
   * siblings, and returns `a0` — the fractional index's very first key. Which the real first
   * root page already has.
   *
   * Two rows then share one key, and from that moment `generateKeyBetween(a, b)` THROWS `a0 >=
   * a0` for anybody who tries to create a page next to either of them. Not once: **for good**.
   * Page creation — the first thing anyone does — stays broken until somebody repairs the
   * column by hand. Nothing warns, because writing a duplicate key is not an error; only using
   * it later is.
   *
   * The tree arrives in well under a second, and this is the only window. Refusing the
   * keystroke costs a user who is faster than their own boot one keypress. The alternative
   * costs them page creation.
   */
  const loaded = tree.isSuccess;

  const loadedRef = useRef(loaded);
  useEffect(() => {
    loadedRef.current = loaded;
  }, [loaded]);

  const createPage = useCallback(
    (parentId: string | null, position: string) => {
      if (!loadedRef.current) return null;

      // A new page appears in an inline-rename state; reveal the tree if it was collapsed so
      // the user is not typing into a hidden row.
      setOpen(true);

      const id = ulid();
      createPageMutate({ id, parentId, position });

      // The row appears in the tree in its correct sorted position, in an inline editing
      // state, BEFORE the network is touched. The round-trip is invisible.
      //
      // `row` is carried in the editing state — not merely the id — so that the row being
      // RENAMED cannot vanish from under the rename. See the `pages` memo: an optimistic row
      // lives in the query cache, and a tree refetch that resolves before the create has
      // drained replaces that cache with a server answer the page is not in yet. The row
      // blinks out; its inline input is uncontrolled; and the half-typed name is destroyed.
      // A row that is being edited is pinned into the tree until the edit is over.
      setEditing({
        id,
        isNew: true,
        row: {
          id,
          parentId,
          position,
          title: 'Untitled',
          icon: null,
          isFavorite: false,
          hasChildren: false,
          dueAt: null,
          updatedAt: new Date().toISOString(),
        },
      });
      setFocused(id);
      return id;
    },
    [createPageMutate],
  );

  /** `Mod+N` — a sibling of the current page, immediately after it. */
  const createSibling = useCallback(
    (relativeTo?: string) => {
      const all = pagesRef.current;
      const anchor = relativeTo ?? focusedRef.current ?? activeIdRef.current;
      if (anchor === undefined || anchor === null || !all.some((p) => p.id === anchor)) {
        return createPage(null, appendPosition(all, null));
      }
      const { parentId, position } = positionAfter(all, anchor);
      return createPage(parentId, position);
    },
    [createPage],
  );

  /** `Mod+Shift+N` — a child of the current page, appended last. Expands the parent. */
  const createChild = useCallback(
    (parentId?: string) => {
      const all = pagesRef.current;
      const parent = parentId ?? focusedRef.current ?? activeIdRef.current ?? null;
      if (parent === null || !all.some((p) => p.id === parent)) {
        return createPage(null, appendPosition(all, null));
      }
      setExpanded(parent, true);
      return createPage(parent, appendPosition(all, parent));
    },
    [createPage],
  );

  /**
   * An empty title commits as "Untitled" — `title` is NOT NULL with that default and the
   * API rejects an empty string, so the client normalizes rather than surfacing a
   * validation error for a blank name (§6, "Renaming").
   *
   * `open` is the `Enter`-on-a-new-page flow: commit the title, open the page, and land the
   * caret in the editor body. That single gesture — `Mod+N`, a name, `Enter`, and you are
   * typing prose — is what makes capture feel instant, and it is criterion 13's
   * keyboard-only path.
   */
  const commitRename = useCallback(
    (id: string, raw: string, open = false) => {
      const title = raw.trim() === '' ? 'Untitled' : raw.trim();
      const page = pagesRef.current.find((p) => p.id === id);
      setEditing(null);

      // THE `undefined` BRANCH SENDS THE RENAME. It used to drop it.
      //
      // The lookup exists only to skip a no-op PATCH when the name has not changed. But it was
      // written as `page !== undefined && page.title !== title`, so a page the local tree
      // happened not to be holding at that instant — a create that had just drained out of the
      // outbox while its tree refetch was still in flight — took the `false` branch and **the
      // name the user had just typed was never sent anywhere**. The page kept the placeholder
      // title "Untitled" for good, and nothing anywhere reported a failure.
      //
      // Not knowing about a page is not a reason to discard a rename for it. The id is real —
      // it is a client-generated ULID and its create is already queued ahead of this in the
      // outbox, which delivers in order. So: if we cannot prove the write is a no-op, send it.
      if (page === undefined || page.title !== title) {
        updatePage({ id, title });
      }

      if (open) {
        // Hand focus OVER, do not merely add a second claimant: without this the row's
        // roving-tabindex effect re-focuses the row the instant inline editing ends, and
        // the caret is yanked straight back out of the document the user is trying to
        // write in.
        setFocused(null);
        // Router state, not a module-level token: the two ends of this hand-off are on
        // opposite sides of a navigation, and the request has to survive the tree
        // unmounting AND StrictMode's deliberate mount/unmount/remount of the editor that
        // answers it. The router already carries state across exactly that gap.
        void navigate(`/p/${id}`, { state: { focusEditor: true } });
      }
    },
    [navigate, updatePage],
  );

  /* ── The row handlers ────────────────────────────────────────────────────────
   *
   * One stable callback per action, shared by every row, each taking the id back from the
   * row that fired it. This is what makes `memo(TreeRow)` real — see TreeRow.tsx's header.
   *
   * `editingRef` exists because `cancelRename` and `renameAndOpen` need to know whether the
   * row is mid-CREATE (Esc commits, Enter opens) — and reading that from `editing` state
   * would put it in the dependency array, re-creating the callback on every edit and
   * undoing the very stability we are buying here.
   */
  const editingRef = useRef(editing);
  useEffect(() => {
    editingRef.current = editing;
  }, [editing]);

  const openPage = useCallback(
    (id: string) => {
      void navigate(`/p/${id}`);
    },
    [navigate],
  );

  const togglePage = useCallback((id: string) => {
    toggleExpanded(id);
  }, []);

  const startRename = useCallback((id: string) => {
    setEditing({ id, isNew: false });
  }, []);

  const renameOnBlur = useCallback(
    (id: string, title: string) => {
      commitRename(id, title);
    },
    [commitRename],
  );

  const renameAndOpen = useCallback(
    (id: string, title: string) => {
      commitRename(id, title, editingRef.current?.isNew === true);
    },
    [commitRename],
  );

  const cancelRename = useCallback(
    (id: string) => {
      // On a create, Esc COMMITS whatever was typed — the row already exists on the server,
      // and silently deleting a page the user just made is the opposite of "never lose
      // data" (§6). On a rename it simply reverts.
      if (editingRef.current?.isNew === true) {
        const page = pagesRef.current.find((candidate) => candidate.id === id);
        commitRename(id, page?.title ?? 'Untitled');
      } else {
        setEditing(null);
      }
    },
    [commitRename],
  );

  const renameAndCreateSibling = useCallback(
    (id: string, title: string) => {
      commitRename(id, title);
      createSibling(id);
    },
    [commitRename, createSibling],
  );

  const addChild = useCallback(
    (id: string) => {
      createChild(id);
    },
    [createChild],
  );

  const toggleFavorite = useCallback(
    (page: TreePage) => {
      updatePage({ id: page.id, isFavorite: !page.isFavorite });
    },
    [updatePage],
  );

  /* ── Drag and drop (§5) ──────────────────────────────────────────────────── */

  const clearHoverTimer = () => {
    if (hoverTimer.current !== null) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };

  const onDragStart = useCallback((event: React.DragEvent, id: string) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', id);
    setDrag({ id, forbidden: subtreeIds(pagesRef.current, id), target: null });
  }, []);

  const onDragOver = useCallback((event: React.DragEvent, id: string, hasKids: boolean) => {
    const current = dragRef.current;
    if (current === null) return;
    event.preventDefault();

    // Every node in the dragged page's own subtree, plus the page itself, renders no
    // indicator and rejects the drop. A UX affordance, not a security control — the server
    // checks too, and a client that never sees a 409 is one that is working.
    if (current.forbidden.has(id)) {
      event.dataTransfer.dropEffect = 'none';
      if (current.target !== null) setDrag({ ...current, target: null });
      return;
    }

    event.dataTransfer.dropEffect = 'move';

    const rect = event.currentTarget.getBoundingClientRect();
    const zone = dropZoneFor(event.clientY - rect.top, rect.height);

    if (current.target?.id !== id || current.target.zone !== zone) {
      setDrag({ ...current, target: { id, zone } });

      // Hovering a collapsed row with children for 600ms expands it, so you can drag into a
      // closed subtree without a separate gesture.
      clearHoverTimer();
      if (zone === 'inside' && hasKids && !expandedRef.current.has(id)) {
        hoverTimer.current = setTimeout(() => {
          setExpanded(id, true);
        }, AUTO_EXPAND_MS);
      }
    }

    // Auto-scroll within 40px of the sidebar's edges.
    const scroller = containerRef.current;
    if (scroller !== null) {
      const box = scroller.getBoundingClientRect();
      if (event.clientY - box.top < AUTO_SCROLL_PX) scroller.scrollTop -= 8;
      else if (box.bottom - event.clientY < AUTO_SCROLL_PX) scroller.scrollTop += 8;
    }
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent, targetId: string) => {
      event.preventDefault();
      clearHoverTimer();

      const state = dragRef.current;
      const all = pagesRef.current;
      setDrag(null);
      if (state === null || state.target === null || state.forbidden.has(targetId)) return;

      const placement = placementFor(all, targetId, state.target.zone, state.id);
      if (placement === null) return;

      const moved = all.find((p) => p.id === state.id);
      if (
        moved !== undefined &&
        moved.parentId === placement.parentId &&
        moved.position === placement.position
      ) {
        return; // it did not actually move
      }

      // One row. `generateKeyBetween(prev, next)`, one field, one request. Never renumber.
      movePage({ id: state.id, parentId: placement.parentId, position: placement.position });
    },
    [movePage],
  );

  const onDragEnd = useCallback(() => {
    clearHoverTimer();
    setDrag(null);
  }, []);

  /** Dropping onto the "Pages" header = move to the root, at the top (§5). */
  const onDropOnRoot = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      clearHoverTimer();

      const state = dragRef.current;
      const all = pagesRef.current;
      setDrag(null);
      if (state === null) return;

      const roots = all.filter((p) => p.parentId === null && p.id !== state.id);
      const placement = placementFor(all, roots[0]?.id ?? state.id, 'before', state.id);

      movePage({
        id: state.id,
        parentId: null,
        position: placement?.position ?? appendPosition(all, null),
      });
    },
    [movePage],
  );

  /* ── Archive ─────────────────────────────────────────────────────────────── */

  const requestArchive = useCallback(
    (page: TreePage) => {
      // Archiving cascades to the whole subtree, SERVER-side. Say so before doing it.
      const count = subtreeIds(pagesRef.current, page.id).size - 1;
      if (count > 0) {
        setConfirmArchive({ page, count });
        return;
      }
      archivePage(page.id);
      if (activeIdRef.current === page.id) void navigate('/');
    },
    [archivePage, navigate],
  );

  /* ── Keyboard (§6) ───────────────────────────────────────────────────────── */

  const focusRow = useCallback((id: string | undefined) => {
    if (id !== undefined) setFocused(id);
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (editing !== null) return;

      const index = rows.findIndex((row) => row.page.id === focused);
      const row = rows[index];

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          focusRow(rows[index + 1]?.page.id ?? rows[0]?.page.id);
          return;

        case 'ArrowUp':
          event.preventDefault();
          focusRow(index <= 0 ? rows.at(-1)?.page.id : rows[index - 1]?.page.id);
          return;

        case 'ArrowRight':
          event.preventDefault();
          if (row === undefined) return;
          if (row.hasChildren && !expanded.has(row.page.id)) setExpanded(row.page.id, true);
          else focusRow(rows[index + 1]?.page.id); // already open → the first child
          return;

        case 'ArrowLeft':
          event.preventDefault();
          if (row === undefined) return;
          if (row.hasChildren && expanded.has(row.page.id)) setExpanded(row.page.id, false);
          else if (row.page.parentId !== null) focusRow(row.page.parentId);
          return;

        case 'Home':
          event.preventDefault();
          focusRow(rows[0]?.page.id);
          return;

        case 'End':
          event.preventDefault();
          focusRow(rows.at(-1)?.page.id);
          return;

        case 'Enter':
          event.preventDefault();
          if (row !== undefined) void navigate(`/p/${row.page.id}`);
          return;

        case 'F2':
          event.preventDefault();
          if (row !== undefined) setEditing({ id: row.page.id, isNew: false });
          return;

        case 'Delete':
          event.preventDefault();
          if (row !== undefined) requestArchive(row.page);
          return;

        case 'Backspace':
          // 110 §7 binds archive to Mod+Backspace; 060 §6 also lists a bare Backspace.
          // Both work; neither surprises.
          event.preventDefault();
          if (row !== undefined) requestArchive(row.page);
          return;

        default:
          break;
      }

      // Type-ahead: jump to the next visible row whose title starts with the typed prefix.
      // Standard tree-widget behaviour, free to implement, and surprisingly useful.
      if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
        const now = Date.now();
        const buffer =
          now - typeAhead.current.at < 500 ? typeAhead.current.buffer + event.key : event.key;
        typeAhead.current = { buffer, at: now };

        const needle = buffer.toLowerCase();
        const from = index + (buffer.length === 1 ? 1 : 0);
        const ordered = [...rows.slice(from), ...rows.slice(0, from)];
        const hit = ordered.find((candidate) =>
          candidate.page.title.toLowerCase().startsWith(needle),
        );

        if (hit !== undefined) {
          event.preventDefault();
          focusRow(hit.page.id);
        }
      }
    },
    [editing, expanded, focused, focusRow, navigate, requestArchive, rows],
  );

  // Global, so that capture is one keystroke away from anywhere — including the editor.
  // Both are Mod-modified, so they cannot collide with typing.
  const hotkeys: Hotkey[] = useMemo(
    () => [
      { key: 'n', mod: true, handler: () => void createSibling() },
      { key: 'n', mod: true, shift: true, handler: () => void createChild() },
      // `110` §7 → Tree: "Mod+Backspace — Archive (with an undo toast)". Bare Backspace and
      // Delete do the same, from the row's own handler above; this is the one the cheatsheet
      // promises, and it must work from wherever the focus happens to be in the sidebar.
      {
        key: 'backspace',
        mod: true,
        handler: () => {
          const id = focusedRef.current ?? activeIdRef.current;
          const page = pagesRef.current.find((candidate) => candidate.id === id);
          if (page !== undefined) requestArchive(page);
        },
      },
    ],
    [createChild, createSibling, requestArchive],
  );
  useHotkeys(hotkeys);

  /* ── Render ──────────────────────────────────────────────────────────────── */

  if (tree.isPending) {
    return (
      <div className="px-[var(--pad-x)] py-2 text-[var(--muted-foreground)]">
        {t('notebook.pages.loading')}
      </div>
    );
  }

  if (tree.isError) {
    return (
      <div className="px-[var(--pad-x)] py-2">
        <p className="text-[var(--destructive)]">{t('notebook.pages.loadError')}</p>
        <button
          type="button"
          onClick={() => void tree.refetch()}
          className="mt-1 underline underline-offset-2"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <>
      <div
        className="flex h-[var(--row-h)] items-center pr-[var(--pad-x)] text-[var(--muted-foreground)]"
        onDragOver={(event) => {
          if (drag !== null) event.preventDefault();
        }}
        onDrop={onDropOnRoot}
      >
        <button
          type="button"
          aria-expanded={open}
          onClick={() => {
            setOpen((previous) => !previous);
          }}
          className="flex h-full min-w-0 flex-1 items-center gap-1.5 px-[var(--pad-x)]"
        >
          <ChevronRight className={cn('size-3 transition-transform', open && 'rotate-90')} />
          <span className="text-xs font-medium tracking-wide uppercase">
            {t('notebook.pages.title')}
          </span>
        </button>
        <button
          type="button"
          aria-label={t('notebook.pages.new')}
          data-testid="new-page"
          onClick={() => void createSibling()}
          className="flex size-5 items-center justify-center rounded-sm hover:bg-[var(--border)]"
        >
          <Plus className="size-3.5" strokeWidth={1.75} />
        </button>
      </div>

      {open && (
        <div
          ref={containerRef}
          role="tree"
          aria-label="Pages"
          data-testid="page-tree"
          className="min-h-0 flex-1 overflow-y-auto px-1 pb-2 outline-none"
          onKeyDown={onKeyDown}
        >
          {rows.length === 0 ? (
            <div className="px-[var(--pad-x)] py-2">
              <p className="font-medium">{t('notebook.welcome.empty')}</p>
              <p className="mb-2 text-[var(--muted-foreground)]">{t('notebook.pages.emptyBody')}</p>
              <button
                type="button"
                onClick={() => void createSibling()}
                className="rounded-md border border-[var(--border)] px-2 py-[var(--pad-y)] hover:bg-[var(--accent)]"
              >
                {t('notebook.pages.new')}
              </button>
            </div>
          ) : (
            rows.map(({ page, depth, hasChildren }) => (
              <TreeRow
                key={page.id}
                page={page}
                depth={depth}
                hasChildren={hasChildren}
                expanded={expanded.has(page.id)}
                active={page.id === activeId}
                focused={page.id === focused}
                // When no row holds focus (the caret is in the editor), the first row is the
                // tree's tab stop — so Tab can get back in without yanking focus out now.
                tabbable={page.id === (focused ?? rows[0]?.page.id)}
                editing={editing?.id === page.id}
                dragging={drag?.id === page.id}
                invalidTarget={drag !== null && drag.forbidden.has(page.id)}
                dropZone={drag?.target?.id === page.id ? drag.target.zone : null}
                pending={pending.has(page.id)}
                // Every one of these is a single shared `useCallback` — NOT a per-row closure.
                // See the note at the top of TreeRow.tsx: with 500 rows, allocating fresh
                // closures here makes `memo` inert and turns an expand/collapse into a ~290ms
                // hitch.
                onOpen={openPage}
                onToggle={togglePage}
                onFocus={setFocused}
                onStartRename={startRename}
                onRename={renameOnBlur}
                onRenameAndOpen={renameAndOpen}
                onCancelRename={cancelRename}
                onRenameAndCreateSibling={renameAndCreateSibling}
                onAddChild={addChild}
                onArchive={requestArchive}
                onToggleFavorite={toggleFavorite}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onDragEnd={onDragEnd}
              />
            ))
          )}
        </div>
      )}

      <AlertDialog
        open={confirmArchive !== null}
        onOpenChange={(next) => {
          if (!next) setConfirmArchive(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('notebook.archiveConfirm.title', { title: confirmArchive?.page.title ?? '' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmArchive !== null &&
                plural('notebook.archiveConfirm.body', confirmArchive.count)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const target = confirmArchive;
                setConfirmArchive(null);
                if (target === null) return;

                archivePage(target.page.id);
                toast.success(t('notebook.archivedToast', { title: target.page.title }));
                if (activeId === target.page.id) void navigate('/');
              }}
            >
              {t('notebook.archiveConfirm.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
